import styled from 'styled-components'
import { ComponentLayout } from '../../../components/layouts/ComponentLayout'
import { Title3 } from '../../../components/styles/Typography'
import { theme } from '../../../theme/theme'
import { useParams } from 'react-router-dom'
import { PreviewGuard } from '../../../components/PreviewGuard'
import { ImageTextBlock } from '../../../components/ImageTextBlock'
import {
  useGetEntriesByContentType,
  useGetPodcastBySlug,
  useGetLocalisedPodcastBySwedishSlug,
  useGetContentWithSwedishFallback,
} from '../../../api/contentful/contentfulQueries'
import { LoadingSpinner } from '../../../components/LoadingSpinner'
import {
  IArticleFields,
  IPodcastEpisode,
  IPodcastEpisodeFields,
  getRelatedContent,
  isPodcast,
} from '@estercare/ester-shared'
import { ListenToPodcastButton } from '../../../components/ListenToPodcastButton'
import { Header } from '../../../components/Header'
import { PodcastList } from '../../../components/PodcastList'
import { useGetQueryParams } from '../../../hooks/useGetQueryParams'
import { useEffect } from 'react'
import { SessionStorageKey, useSessionStorage } from '../../../hooks/useSessionStorage'
import { KnowledgebankFilterProps } from './Index'
import { useTranslation } from 'react-i18next'

export const Podcast = () => {
  const { t } = useTranslation()
  const [, setKnowledgebankFilter] = useSessionStorage<KnowledgebankFilterProps>(SessionStorageKey.KNOWLEDGEBANK_FILTER)
  const { id } = useParams()
  if (!id) throw new Error('No slug provided')
  const queryParams = useGetQueryParams(['y', 'podcast'])

  useEffect(() => {
    if (queryParams.y && queryParams.podcast) {
      setKnowledgebankFilter({
        savedPodcastIndex: Number(queryParams.podcast),
        savedScrollPosition: Number(queryParams.y),
        browserIndex: window.history.length,
      })
    }
  }, [queryParams.podcast, queryParams.y, setKnowledgebankFilter])

  const podcastQuery = useGetPodcastBySlug(id)

  //This is a temporary fallback solution until we have the new healthplan without Swedish slugs
  const { data: podcastEpisode, isLoading: podcastIsLoading } = useGetContentWithSwedishFallback<IPodcastEpisode>(
    podcastQuery,
    useGetLocalisedPodcastBySwedishSlug,
    id
  )

  const allPodcastEpisodes = useGetEntriesByContentType<IPodcastEpisodeFields>('podcastEpisode').data?.items

  if (podcastIsLoading) return <LoadingSpinner />
  if (!isPodcast(podcastEpisode)) return <PreviewGuard />

  const { episodeNumber, title, description, featuredImage, spotifyEpisodeId } = podcastEpisode.fields
  if (!description || !featuredImage || !title) return <PreviewGuard />

  const relatedPodcastEpisodes = allPodcastEpisodes
    ? getRelatedContent(podcastEpisode, allPodcastEpisodes, 4)?.map((entry) => entry.fields)
    : null

  return (
    <>
      <Header
        title={t('employee.knowledgeBank.episodeTitle', { episodeNumber, title })}
        backButtonLink="/portal/resource-center"
        backgroundColor={theme.color.plum}
        textColor={theme.color.plumLight}
      />
      <ImageTextBlock
        imageUrl={`https:${featuredImage.fields.file.url}`}
        imageOrientation="left"
        title={t('employee.knowledgeBank.aboutEpisode')}
        text={description}
      />
      <ComponentLayout backgroundColor={theme.color.beigeDark}>
        <PodcastButtonContainer>
          <ListenToPodcastButton
            subtitle={t('employee.knowledgeBank.episodeTitle', { episodeNumber, title })}
            href={`https://open.spotify.com/episode/${spotifyEpisodeId}`}
            width="562px"
          />
        </PodcastButtonContainer>
      </ComponentLayout>
      {relatedPodcastEpisodes && (
        <ComponentLayout backgroundColor={theme.color.beigeLight}>
          <StyledTitle3>{t('employee.knowledgeBank.relatedPodcasts')}</StyledTitle3>
          <PodcastList podcasts={relatedPodcastEpisodes as IArticleFields[]} title="" />
        </ComponentLayout>
      )}
    </>
  )
}

const PodcastButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`

const StyledTitle3 = styled(Title3)`
  text-align: center;
  margin-bottom: ${theme.spacing.mediumLarge}px;
`
